import { useClose } from "@headlessui/react"
import { useCombobox } from "downshift"
import { useEffect } from "react"

import { PopoverTextInput } from "../../../base/overlay/Popover"
import PopoverList from "../PopoverList"
import PopoverListEmpty from "../PopoverListEmpty"
import PopoverSelectListItem from "./PopoverSelectListItem"
import { PopoverSelectComboboxProps } from "./types"
import { PopoverComboboxItem } from "../types"

function PopoverSelectCombobox<
  T extends PopoverComboboxItem = PopoverComboboxItem,
>({
  items,
  selected,
  onSelect,
  onChangeQuery,
  maxLength,
}: PopoverSelectComboboxProps<T>) {
  const close = useClose()

  useEffect(() => {
    return () => {
      onChangeQuery("")
    }
  }, [onChangeQuery])

  const { getMenuProps, highlightedIndex, getItemProps, getInputProps } =
    useCombobox<T>({
      defaultInputValue: "",
      defaultHighlightedIndex: 0,
      onInputValueChange({ inputValue }) {
        onChangeQuery(inputValue ?? "")
      },
      items,
      selectedItem: selected,
      onSelectedItemChange({ selectedItem }) {
        onSelect(selectedItem)
        close()
      },
      itemToString: (item) => item?.label ?? "",
      itemToKey: (item) => item?.key ?? "",
      stateReducer: (_, { type, changes }) => {
        const defaultChanges = {
          ...changes,
          isOpen: true,
        }
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEscape:
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputBlur:
            return {
              ...defaultChanges,
              inputValue: "",
            }
          default:
            return defaultChanges
        }
      },
      isOpen: true,
    })

  return (
    <>
      <PopoverTextInput
        autoFocus
        data-testid="popover-select-combobox-input"
        maxLength={maxLength}
        {...getInputProps()}
      />
      <PopoverList {...getMenuProps()}>
        {items.length === 0 && <PopoverListEmpty />}
        {items.map((item, index) => (
          <PopoverSelectListItem
            key={item.key}
            label={item.label}
            accessory={item.accessory}
            content={item.content}
            isHighlighted={index === highlightedIndex}
            isSelected={item.key === selected?.key}
            {...getItemProps({ item, index })}
          />
        ))}
      </PopoverList>
    </>
  )
}

export default PopoverSelectCombobox
